/* eslint-disable no-unused-vars */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import axiosApis from 'common/apis/axiosApis';
import { queryClient } from 'common/const/queryClient';

// Param store for the superadmin system-wide Aircraft list (BT-9798).
// `status` uses '' to mean "any status" (0=Provisional, 1=Active, 2=Deactive), mirroring the
// Users module's roleId convention.
export const useAllSystemAircraftParamStore = create((set) => ({
  min: 0,
  max: 25,
  searchText: '',
  activePage: 1,
  status: '',
  // Selected account filter: the { company_id, account_name } option, or null for "all accounts".
  account: null,
  setMin: (min) => set({ min }),
  setMax: (max) => set({ max }),
  setSearchText: (searchText) => set({ searchText }),
  setActivePage: (activePage) => set({ activePage }),
  setStatus: (status) => set({ status }),
  setAccount: (account) => set({ account }),
  reset: () => set({
    min: 0,
    max: 25,
    searchText: '',
    activePage: 1,
    status: '',
    account: null,
  }),
}));

async function getAllSystemAircraftData({ queryKey }) {
  const [_, {
    searchText, min, max, status, companyId,
  }] = queryKey;
  const userDetails = JSON.parse(localStorage.getItem('adminDetails'));

  const params = new URLSearchParams({ min, max, search_text: searchText });
  if (status !== '' && status != null) {
    params.set('status', String(status));
  }
  if (companyId !== '' && companyId != null) {
    params.set('company_id', String(companyId));
  }

  return axiosApis.get(`/admin/getAllSystemAircraft?${params.toString()}`, {
    headers: {
      Authorization: userDetails.token,
    },
  }).then(async (response) => {
    await response;
    return response;
  });
}

export const getQueryKey = ({
  searchText,
  min,
  max,
  activePage,
  status,
  companyId,
}) => ['getAllSystemAircraft', {
  searchText,
  min,
  max,
  activePage,
  status,
  companyId,
}];

export const invalidateAllSystemAircraft = (params) => {
  if (params) {
    queryClient.invalidateQueries(getQueryKey(params));
  } else {
    queryClient.invalidateQueries(['getAllSystemAircraft']);
  }
};

export function useAllSystemAircraft() {
  const queryParams = useAllSystemAircraftParamStore(useShallow(
    (state) => ({
      min: state.min,
      max: state.max,
      searchText: state.searchText,
      activePage: state.activePage,
      status: state.status,
      companyId: state.account?.company_id ?? null,
    }),
  ));

  const { reset } = useAllSystemAircraftParamStore(useShallow((state) => ({
    reset: state.reset,
  })));

  const key = useMemo(() => getQueryKey(queryParams), [queryParams]);

  const {
    isPending, isError, error, data, isFetching, isPlaceholderData,
  } = useQuery({
    queryKey: key,
    queryFn: getAllSystemAircraftData,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 15,
  });

  // When this unmounts, reset the params store
  useEffect(() => () => {
    reset();
  }, [reset]);

  return useMemo(() => ({
    isPending,
    isError,
    error,
    isFetching,
    isPlaceholderData,
    aircraft: data?.data?.aircraft || [],
    totalAircraftCount: data?.data?.totalAircraftCount || 0,
  }), [data, error, isError, isFetching, isPending, isPlaceholderData]);
}
